import * as React from "react";
import { cn } from "@/lib/cn";

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  /** A `chart-tone--*` suffix - `cat-1`, `stop`, … */
  tone: string;
}

/**
 * A ring and the numbers beside it.
 *
 * ── Why the rows are not optional ──────────────────────────────────────────
 *
 * **Nobody reads a value off a ring.** The eye is poor at comparing angles and
 * worse at arc lengths, which is the whole case against pie charts and it does
 * not stop being true because the middle is hollow. So the ring carries the
 * *shape* of the split - one dominant type, or an even spread - and the rows
 * carry the figures. They are one component because hovering either has to
 * light the other; a legend that cannot be pointed at is a lookup table.
 *
 * This is what `app.css` has described since before there was a component to
 * draw it. The class names here are that stylesheet's, not new ones.
 *
 * ── Why the gaps matter ────────────────────────────────────────────────────
 *
 * Segments are separated by a gap that shows the track through, so two adjacent
 * tones read apart without depending on hue at all. The categorical palette is
 * six and a site can configure eight work types, so two of them *will* share a
 * colour - the gap and the row beneath are what keep that from being ambiguous.
 *
 * ── Geometry ───────────────────────────────────────────────────────────────
 *
 * One `<circle>` per slice with `stroke-dasharray`, rotated so the arcs start at
 * twelve o'clock. Cheaper than arc paths and exact: a dash of `share * C` needs
 * no trigonometry and cannot accumulate rounding around the ring.
 */
export function DonutChart({
  slices,
  centerLabel,
  className,
}: {
  slices: DonutSlice[];
  /** What the middle's figure counts - "visits". The total is computed here. */
  centerLabel?: string;
  className?: string;
}) {
  const [hovered, setHovered] = React.useState<string | null>(null);

  const total = slices.reduce((sum, slice) => sum + slice.value, 0);

  if (slices.length === 0 || total === 0) {
    return <p className="chart-empty">Nothing recorded in this window yet.</p>;
  }

  // A 36-unit box with a radius chosen so the circumference is almost exactly
  // 100 - the same trick every CSS donut uses, because it makes a dasharray
  // read as a percentage rather than as a number nobody can check.
  const RADIUS = 15.9155;
  const CIRCUMFERENCE = 100;
  // Shown as a gap, taken off each arc. Only where there is more than one slice:
  // a single type would otherwise be a ring with a notch cut in it for no
  // reason.
  const GAP = slices.length > 1 ? 1.2 : 0;

  let offset = 0;

  return (
    <div
      className={cn("donut", className)}
      onMouseLeave={() => setHovered(null)}
      role="group"
      aria-label={slices.map((slice) => `${slice.label}: ${slice.value}`).join("; ")}
    >
      <div className="donut__ring">
        <svg viewBox="0 0 36 36" className="donut__figure" role="img" aria-hidden>
          <circle cx="18" cy="18" r={RADIUS} fill="none" strokeWidth="4" className="donut__track" />

          {slices.map((slice) => {
            const share = (slice.value / total) * CIRCUMFERENCE;
            const arc = Math.max(0, share - GAP);
            // `-25` puts zero at twelve o'clock; each slice then starts where
            // the last one ended.
            const rotation = -25 + offset;
            offset += share;

            return (
              <circle
                key={slice.key}
                cx="18"
                cy="18"
                r={RADIUS}
                fill="none"
                strokeWidth="4"
                strokeDasharray={`${arc} ${CIRCUMFERENCE - arc}`}
                strokeDashoffset={-rotation}
                transform="rotate(-90 18 18)"
                className={cn(
                  "donut__seg",
                  `chart-tone--${slice.tone}`,
                  hovered && hovered !== slice.key && "donut__seg--dim"
                )}
              />
            );
          })}
        </svg>

        <div className="donut__center">
          <span className="donut__center-value tnum">{total}</span>
          {centerLabel && <span className="donut__center-label">{centerLabel}</span>}
        </div>
      </div>

      <ul className="donut__rows">
        {slices.map((slice) => (
          <li
            key={slice.key}
            className={cn(
              "donut__row",
              `chart-tone--${slice.tone}`,
              hovered && hovered !== slice.key && "donut__row--dim"
            )}
            onMouseEnter={() => setHovered(slice.key)}
          >
            <i className="donut__swatch" aria-hidden />
            <span className="donut__row-label">{slice.label}</span>
            <span className="donut__row-value tnum">{slice.value}</span>
            <span className="donut__row-share tnum">
              {Math.round((slice.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
